import { createPortal } from 'react-dom'
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'

interface MobileDockShellProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
  /**
   * Horizontal gutter applied via paddingLeft/paddingRight (default 12px).
   * The safe-area bottom inset is always included regardless of this value.
   */
  gutterX?: number
  /**
   * z-index for the fixed layer (default 30 — matches existing dock z-level).
   */
  zIndex?: number
  /**
   * Where the safe-area-inset-bottom padding lands.
   *
   * - `'shell'` (default): padding-bottom on the shell — children float above
   *   the inset zone. The space below children is transparent and shows
   *   whatever is behind the shell.
   * - `'children'`: shell has no bottom padding; the inset value is exposed
   *   as a CSS variable (`--mobile-dock-safe-bottom`) so a child surface can
   *   extend its background through the inset zone. Use when the dock should
   *   appear flush with the screen bottom with the home indicator sitting
   *   inside the dock's surface (no visible gap below it).
   *
   * The env() call always lives inside this primitive — CLAUDE.md rule 8 /
   * no-direct-safe-area lint forbids it elsewhere.
   */
  insetTarget?: 'shell' | 'children'
}

/**
 * Fixed-bottom shell for mobile dock / tab-bar / floating-action elements.
 *
 * Handles:
 * - `position: fixed; bottom: 0; left: 0; right: 0`
 * - `paddingBottom: env(safe-area-inset-bottom, 0px)` so content clears
 *   the iOS home indicator
 *
 * - escaping transformed ancestors, by rendering through a portal to
 *   `document.body` (see below)
 *
 * Rule: any new fixed-bottom mobile component MUST compose this shell instead
 * of setting its own `bottom` / `paddingBottom` values. Chrome DevTools does
 * NOT simulate safe-area insets — regressions on real iOS Safari have already
 * recurred three times (FOR-1544, FOR-1584, FOR-1597).
 *
 * ── Why the portal ──
 * `position: fixed` resolves against the nearest ancestor with a transform,
 * filter, perspective, `will-change: transform`, or `contain: paint` — not the
 * viewport. Every page in this app is wrapped in `.animate-fade-up`, whose
 * keyframes end on `translateY(0)` with `animation-fill-mode: both`, so the
 * computed transform stays `matrix(1,0,0,1,0,0)` forever after the entrance
 * animation. That is a non-`none` transform, so it becomes the containing
 * block: the Customers FAB was being laid out ~3500px down a page whose
 * viewport is 844px tall, i.e. permanently off-screen and unreachable, with
 * no page scroll to bring it back.
 *
 * Portaling to `document.body` puts the shell outside every page wrapper, so
 * `fixed` means the viewport again. Doing it here rather than at each call
 * site is deliberate — this primitive exists precisely so positioning bugs
 * have one place to be fixed.
 */
export function MobileDockShell({
  children,
  gutterX = 12,
  zIndex = 30,
  insetTarget = 'shell',
  style,
  ...props
}: MobileDockShellProps) {
  const insetExpr = 'env(safe-area-inset-bottom, 0px)'
  const insetTargetVars =
    insetTarget === 'children'
      ? ({ ['--mobile-dock-safe-bottom' as string]: insetExpr } as CSSProperties)
      : undefined

  const shellStyle: CSSProperties = {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex,
    paddingLeft: gutterX,
    paddingRight: gutterX,
    paddingBottom: insetTarget === 'shell' ? insetExpr : 0,
    ...insetTargetVars,
    ...style,
  }

  const shell = (
    <div style={shellStyle} {...props}>
      {children}
    </div>
  )

  // No mount gate: this app uses createRoot, not hydrateRoot, so there is no
  // hydration mismatch to avoid — gating cost an extra render plus a paint with
  // the dock missing on every route mount. The `document` check is kept only so
  // a non-DOM environment degrades to nothing rather than throwing.
  if (typeof document === 'undefined') return null
  return createPortal(shell, document.body)
}
